/**
 * `OnchainActivityPanel` — the Overview blockchain-activity widget.
 *
 * Shows recent cross-chain activity as either a virtualised **Table**
 * (`@authe/tx-table`) or an interactive multichain **Graph** (`@authe/tx-graph`),
 * with a Live / Auto / Off update control (`@authe/ui-core`). View + live-mode
 * choices persist via the shared `DisplayPrefsStore`. All field names,
 * formatting, view-mode and live-mode vocabulary come from `@authe/fe-commons` +
 * the lower packages, so this surface stays consistent with the rest of the app.
 *
 * This is the *onchain* surface — recent blockchain transactions + the
 * interaction graph. It is intentionally separate from the in-app event inbox at
 * `/activity` (`@authe/notification-center`), which tracks app/collaboration
 * events. Two distinct feeds, clearly named.
 */
import * as React from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Activity, RefreshCw, Share2, Table2, Wallet as WalletIcon } from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  ErrorState,
  LiveModeControl,
  Skeleton,
  ViewModeToggle,
  cn,
  useDisplayPref,
} from "@authe/ui-core";
import { TransactionTable, txRowId, type TxRow } from "@authe/tx-table";
import { GraphCanvas, type TxGraphNode, type GraphShareState } from "@authe/tx-graph";
import { AccessWarningBanner, type AccessWarning } from "@authe/saved-views";
import {
  ACTIVITY_VIEWS,
  type ActivityView,
  type GraphViewMode,
  type LiveMode,
} from "@authe/fe-commons/display";
import { ROUTES } from "@/config";
import { displayPrefs } from "@/app/prefs";
import { isStreamingConfigured } from "@/app/streaming";
import { GraphSavedViewsBar } from "@/features/graphs/GraphSavedViewsBar";
import { useOnchainActivity } from "./useOnchainActivity";
import { OnchainLiveFeed } from "./OnchainLiveFeed";

import "@xyflow/react/dist/style.css";

const VIEW_LABELS: Record<ActivityView, React.ReactNode> = { table: "Table", graph: "Graph" };
const VIEW_ICONS: Record<ActivityView, React.ReactNode> = {
  table: <Table2 className="h-3.5 w-3.5" />,
  graph: <Share2 className="h-3.5 w-3.5" />,
};
const VIEW_OPTIONS = ACTIVITY_VIEWS.map((value) => ({
  value,
  label: VIEW_LABELS[value],
  icon: VIEW_ICONS[value],
}));

/** Pull a bare EVM address out of a chain-scoped node id (`chain#0x…`). */
function addressFromNodeId(id: string): string | undefined {
  const last = id.includes("#") ? id.slice(id.lastIndexOf("#") + 1) : id;
  return /^0x[0-9a-fA-F]{40}$/.test(last) ? last : undefined;
}

const GRAPH_HEIGHT = "h-[440px]";

export function OnchainActivityPanel(): React.ReactElement {
  const navigate = useNavigate();
  const liveAvailable = isStreamingConfigured;

  const [view, setView] = useDisplayPref<ActivityView>(
    () => displayPrefs.getActivityView(),
    (v) => displayPrefs.setActivityView(v),
  );
  const [liveMode, setLiveMode] = useDisplayPref<LiveMode>(
    () => displayPrefs.getLiveMode(),
    (v) => displayPrefs.setLiveMode(v),
  );
  // Honour the stored choice, but never run Live without an endpoint.
  const effectiveMode: LiveMode = liveMode === "live" && !liveAvailable ? "interval" : liveMode;

  const feed = useOnchainActivity({ liveMode: effectiveMode });
  const liveActive = liveAvailable && effectiveMode === "live";

  // Graph layout mode is lifted here so the saved-views bar can snapshot it and
  // the canvas can render either the live feed or an applied saved snapshot.
  const [storedMode, setGraphMode] = useDisplayPref<GraphViewMode>(
    () => displayPrefs.getGraphViewMode(),
    (m) => displayPrefs.setGraphViewMode(m),
  );
  const graphMode = GRAPH_MODES.includes(storedMode) ? storedMode : "hierarchical";

  // A saved graph view, when applied, replaces the live feed on the canvas;
  // resetting it (`null`) returns to the live graph.
  const [appliedGraph, setAppliedGraph] = React.useState<GraphShareState | null>(null);
  const [warnings, setWarnings] = React.useState<AccessWarning[]>([]);

  // What the "Save as view" action snapshots: the applied view if one is open,
  // otherwise the current live feed graph + layout.
  const currentGraph = React.useMemo<GraphShareState>(
    () =>
      appliedGraph ?? {
        viewMode: graphMode,
        nodes: feed.graph.nodes,
        edges: feed.graph.edges,
      },
    [appliedGraph, graphMode, feed.graph.nodes, feed.graph.edges],
  );

  const onRowClick = React.useCallback(
    (row: TxRow) => {
      const id = txRowId(row);
      if (id) void navigate({ to: "/transactions/$id", params: { id } });
    },
    [navigate],
  );

  const onNodeClick = React.useCallback(
    (node: TxGraphNode) => {
      const address = addressFromNodeId(node.id);
      if (address) void navigate({ to: "/addresses/$id", params: { id: address } });
    },
    [navigate],
  );

  return (
    <Card className="mt-6">
      <CardHeader className="flex-row flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <CardTitle>{feed.hasWallet ? "Your onchain activity" : "Live blockchain activity"}</CardTitle>
          <CardDescription>
            {feed.hasWallet
              ? "Recent cross-chain history for your connected wallets, discovered across their active chains."
              : "The latest cross-chain transactions. Connect a wallet to see your own activity."}
          </CardDescription>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {liveActive && (
            <OnchainLiveFeed
              enabled
              onRows={feed.setStreamedRows}
              count={feed.streamedCount}
              addresses={feed.addresses}
            />
          )}
          <LiveModeControl mode={liveMode} onChange={setLiveMode} liveAvailable={liveAvailable} size="sm" />
          <ViewModeToggle<ActivityView>
            aria-label="Activity view"
            options={VIEW_OPTIONS}
            value={view}
            onChange={setView}
            size="sm"
          />
          {view === "graph" && (
            <GraphSavedViewsBar
              currentGraph={currentGraph}
              onApplyGraph={setAppliedGraph}
              onWarnings={setWarnings}
              saveDisabled={!appliedGraph && feed.graph.nodes.length === 0}
            />
          )}
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="h-7 w-7"
            onClick={() => feed.refetch()}
            disabled={feed.isFetching}
            aria-label="Refresh activity"
            title="Refresh"
          >
            <RefreshCw className={cn("h-3.5 w-3.5", feed.isFetching && "animate-spin")} />
          </Button>
          {!feed.hasWallet && (
            <Button asChild size="sm" variant="outline">
              <Link to={ROUTES.wallet}>
                <WalletIcon className="h-4 w-4" />
                Connect wallet
              </Link>
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent>
        {view === "table" ? (
          <TransactionTable
            rows={feed.rows}
            isLoading={feed.isLoading}
            isFetching={feed.isFetching}
            error={feed.error}
            onRetry={feed.refetch}
            onRowClick={onRowClick}
            enableToolbar={false}
            density="compact"
            height={GRAPH_HEIGHT}
            emptyTitle="No recent activity"
            emptyDescription={
              feed.hasWallet
                ? "We couldn't find recent transactions for the connected wallets."
                : "No transactions returned from the preview chains right now."
            }
          />
        ) : (
          <>
            <AccessWarningBanner warnings={warnings} className="mb-3" />
            <OnchainGraph
              feed={feed}
              onNodeClick={onNodeClick}
              hasWallet={feed.hasWallet}
              appliedGraph={appliedGraph}
              graphMode={graphMode}
              onGraphModeChange={setGraphMode}
              onResetToLive={() => setAppliedGraph(null)}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}

interface OnchainGraphProps {
  feed: ReturnType<typeof useOnchainActivity>;
  onNodeClick: (node: TxGraphNode) => void;
  hasWallet: boolean;
  /** When set, render this saved snapshot instead of the live feed graph. */
  appliedGraph: GraphShareState | null;
  graphMode: GraphViewMode;
  onGraphModeChange: (mode: GraphViewMode) => void;
  onResetToLive: () => void;
}

// Lightweight React Flow layouts only. The heavy "force" engine needs the
// optional `react-force-graph-2d` peer, which the dashboard doesn't install, so
// it's intentionally left out of the Overview's switcher.
const GRAPH_MODES: GraphViewMode[] = ["hierarchical", "temporal"];

function OnchainGraph({
  feed,
  onNodeClick,
  hasWallet,
  appliedGraph,
  graphMode,
  onGraphModeChange,
  onResetToLive,
}: OnchainGraphProps): React.ReactElement {
  const showingSnapshot = appliedGraph != null;
  const nodes = showingSnapshot ? appliedGraph.nodes ?? [] : feed.graph.nodes;
  const edges = showingSnapshot ? appliedGraph.edges ?? [] : feed.graph.edges;
  // Clamp a previously-persisted unavailable mode (e.g. "force") to a flow layout.
  const mode = showingSnapshot ? appliedGraph.viewMode ?? graphMode : graphMode;

  if (!showingSnapshot && feed.isLoading) {
    return <Skeleton className={cn("w-full rounded-md", GRAPH_HEIGHT)} />;
  }
  if (!showingSnapshot && feed.error) {
    return <ErrorState error={feed.error} onRetry={feed.refetch} />;
  }
  if (nodes.length === 0) {
    return (
      <div className={cn("flex items-center justify-center rounded-md border", GRAPH_HEIGHT)}>
        <EmptyState
          icon={<Activity className="h-6 w-6" />}
          title={showingSnapshot ? "Empty saved view" : "No relationships to graph"}
          description={
            showingSnapshot
              ? "This saved view has no graph payload. Reset to see live activity."
              : hasWallet
                ? "No cross-chain interactions found for the connected wallets yet."
                : "No interaction graph returned from the preview chains right now."
          }
        />
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {showingSnapshot && (
        <div className="flex items-center justify-between rounded-md border border-dashed bg-muted/40 px-3 py-1.5 text-xs text-muted-foreground">
          <span>Viewing a saved graph snapshot — live updates are paused.</span>
          <Button type="button" size="sm" variant="ghost" className="h-6" onClick={onResetToLive}>
            Reset to live
          </Button>
        </div>
      )}
      <div className={cn("overflow-hidden rounded-md border", GRAPH_HEIGHT)}>
        <GraphCanvas
          nodes={nodes}
          edges={edges}
          viewMode={mode}
          onViewModeChange={onGraphModeChange}
          showViewModeToggle
          viewModes={GRAPH_MODES}
          onNodeClick={onNodeClick}
          showControls
          showMiniMap={false}
          fitView
          className="h-full w-full"
        />
      </div>
    </div>
  );
}
